<template>
  <div
    class="dropdown-overlay mob-dropdown"
    :class="{ on: isMobile }"
    @click.self="closeDropdown"
    ref="refRoot"
    tabindex="0"
  >
    <div ref="dropdownRef" class="dropdown-content" @click="closeDropdown">
      <slot></slot>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, nextTick, watch } from 'vue';

import { useKeyPress } from '@/composables/shared/useKeyPress';

const emit = defineEmits(['close']);

const props = defineProps({
  targetEl: {
    type: HTMLElement,
    default: null,
  },
  isVisible: {
    type: Boolean,
  },
});

const dropdownRef = ref<HTMLElement | null>(null);
const refRoot = ref<HTMLElement | null>(null);
const isMobile = ref(window.innerWidth <= 768);

const handleResize = () => {
  isMobile.value = window.innerWidth <= 768;
};

onMounted(() => window.addEventListener('resize', handleResize));
onUnmounted(() => window.removeEventListener('resize', handleResize));
const closeDropdown = () => {
  emit('close');
};

const updateDropdownPosition = async () => {
  // 768px 이하일때는 동작 안함
  if (isMobile.value) return;

  await nextTick();
  if (dropdownRef.value && props.targetEl) {
    const targetRect = props.targetEl.getBoundingClientRect();
    const dropdownRect = dropdownRef.value.getBoundingClientRect();

    let left = targetRect.right - dropdownRect.width;

    if (left + dropdownRect.width > window.innerWidth - 8) {
      left = targetRect.left - dropdownRect.width - 4;
    }

    let top = targetRect.bottom + 4;
    if (top + dropdownRect.height > window.innerHeight - 16) {
      top = targetRect.top - dropdownRect.height - 4;
    }

    if (top < 16) {
      top = 16;
    }

    if (top + dropdownRect.height > window.innerHeight - 16) {
      top = window.innerHeight - dropdownRect.height - 16;
    }

    dropdownRef.value.style.left = `${left}px`;
    dropdownRef.value.style.top = `${top}px`;
    dropdownRef.value.style.maxHeight = `${window.innerHeight - 32}px`;
    dropdownRef.value.style.overflowY = 'auto';
  }
};

useKeyPress(refRoot, {
  Escape: () => emit('close'),
});

onMounted(() => {
  refRoot.value?.focus();
  document.body.style.overflow = 'hidden';
  window.addEventListener('resize', updateDropdownPosition);
  updateDropdownPosition();
});

onUnmounted(() => {
  document.body.style.overflow = '';
  window.removeEventListener('resize', updateDropdownPosition);
});

watch(
  () => props.isVisible,
  (val) => {
    if (val) updateDropdownPosition();
  }
);
</script>

<style scoped lang="scss">
.dropdown-overlay {
  position: fixed;
  inset: 0;
  z-index: $z-dropdown;
}

.dropdown-content {
  position: absolute;
  display: flex;
  flex-direction: column;
  width: fit-content;
  padding: 0.5rem;
  border: 1px solid var(--border-default);
  border-radius: 12px;
  background: var(--surface-default);
  box-shadow: $shadow-dropdown;
  overflow-y: auto;
}
.mob-dropdown {
  &.on {
    background: $color-transparent-dark60;
    .dropdown-content {
      bottom: 0;
      left: 0;
      width: 100%;
      border-radius: 12px 12px 0 0;
      transform: translateY(0);
    }
  }
  @include respond-to(mobile-landscape) {
    .dropdown-content {
      border: none;
      padding: 24px 12px;
      transform: translateY(100%);
      transition: all 0.3s;
    }
  }
}
</style>
